import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import Box from "@mui/material/Box";
import Typography from "@mui/material/Typography";
import { makeStyles } from "@mui/styles";

import LoginHeader from "pages/Login/components/LoginHeader";
import LoginCard from "pages/Login/components/LoginCard";
import SideCard from "pages/Login/components/SideCard";

import GridContainer from "components/common/GridContainer";
import GridItem from "components/common/GridItem";
import useIsMobile from "utils/helpers/mediaQueryHelper";
import userService from "services/User/userService";
import { useNavigate } from "react-router-dom";
import { ROUTES } from "config";
import PrimaryButton from "components/common/DButton";
import { Alert } from "@mui/material";
import DTextField from "components/common/DTextField";
import { IconButton, InputAdornment, Stack } from "@mui/material";
import {
    Visibility,
    VisibilityOff,
    CheckCircleOutlineOutlined,
    // CheckCircleOutline,
    CancelOutlined
} from "@mui/icons-material";

const styles = {
    root: {
        margin: "auto",
        display: "flex",
        justifyContent: "center",
        alignItems: "center",
        minHeight: "100vh",
        flexDirection: "column",
        background: "#fff"
    }
};

const useStyles = makeStyles(styles);

const checkRules = (password = "") => [
    {
        label: "At least 8 characters",
        met: password.length >= 8
    },
    {
        label: "At least one uppercase letter (A-Z)",
        met: /[A-Z]/.test(password)
    },
    {
        label: "At least one lowercase letter (a-z)",
        met: /[a-z]/.test(password)
    },
    {
        label: "At least one digit (0-9)",
        met: /[0-9]/.test(password)
    },
    {
        label: "At least one special character (@$!%*?&#...)",
        met: /[^a-zA-Z0-9]/.test(password)
    }
];

export default function ResetPassword() {
    const classes = useStyles();
    const isMobile = useIsMobile();
    const { token } = useParams();
    const navigate = useNavigate();

    const [isValidToken, setIsValidToken] = useState(null);
    const [password, setPassword] = useState("");
    const [confirmPassword, setConfirmPassword] = useState("");
    const [errors, setErrors] = useState({});
    const [loading, setLoading] = useState(false);
    const [isSubmitted, setIsSubmitted] = useState(false);
    const [showPassword, setShowPassword] = useState(false);
    const [showConfirmPassword, setShowConfirmPassword] = useState(false);

    const handleSubmit = async (e) => {
        e.preventDefault();

        if (!validateForm()) {
            return;
        }

        try {
            setLoading(true);
            setErrors({});

            await userService.resetPassword(token, password);

            setIsSubmitted(true);
        } catch (error) {
            console.error("Reset password error:", error);

            setErrors({
                submit:
                    error?.response?.data?.message ||
                    "Unable to reset password. Please try again."
            });
        } finally {
            setLoading(false);
        }
    };

    const validateForm = () => {
        const newErrors = {};

        if (!password) {
            newErrors.password = "New password is required.";
        } else if (password.length < 8) {
            newErrors.password = "Password must be at least 8 characters.";
        } else if (!/[A-Z]/.test(password)) {
            newErrors.password =
                "Password must contain at least one uppercase letter.";
        } else if (!/[a-z]/.test(password)) {
            newErrors.password =
                "Password must contain at least one lowercase letter.";
        } else if (!/[0-9]/.test(password)) {
            newErrors.password = "Password must contain at least one number.";
        } else if (!/[^a-zA-Z0-9]/.test(password)) {
            newErrors.password =
                "Password must contain at least one special character.";
        }

        if (!confirmPassword) {
            newErrors.confirmPassword = "Please confirm your password.";
        } else if (password !== confirmPassword) {
            newErrors.confirmPassword = "Passwords do not match.";
        }

        setErrors(newErrors);

        return Object.keys(newErrors).length === 0;
    };

    const passwordRules = checkRules(password);
    const isPasswordTouched = password.length > 0;

    useEffect(() => {
        const validateToken = async () => {
            try {
                const response = await userService.validateResetToken(token);
                console.log("Token validation response:", { response, token });

                setIsValidToken(response?.data?.isValid);
            } catch (error) {
                console.error("Token validation error:", error);
                setIsValidToken(false);
            }
        };

        validateToken();
    }, [token]);

    console.log("isValidToken:", { isValidToken });

    const renderLoading = isValidToken === null && (
        <GridItem sx={{ flex: 1 }}>
            <Box
                sx={{
                    //minHeight: "100vh",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center"
                }}
            >
                <Typography color="text.secondary">
                    Validating reset link...
                </Typography>
            </Box>
        </GridItem>
    );

    const renderInvalid = !isValidToken && (
        <GridItem sx={{ flex: 1 }}>
            <Box
                sx={{
                    // minHeight: "100vh",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    px: 2
                }}
            >
                <Box sx={{ textAlign: "center", maxWidth: 400 }}>
                    <Typography variant="h6" gutterBottom>
                        Invalid Reset Link
                    </Typography>

                    <Typography variant="body2" color="text.secondary">
                        This password reset link is invalid, expired, or has
                        already been used.
                    </Typography>

                    <Box sx={{ mt: 3 }}>
                        <PrimaryButton onClick={() => navigate(ROUTES.LOGIN)}>
                            Back to Login
                        </PrimaryButton>
                    </Box>
                </Box>
            </Box>
        </GridItem>
    );

    const renderValid = isValidToken && (
        <GridItem sx={{ flex: 1 }}>
            <GridContainer direction="column">
                <GridItem>
                    <LoginHeader />
                </GridItem>
                <GridItem>
                    <Typography
                        variant="body2"
                        color="text.secondary"
                        mb={2}
                        sx={{ fontWeight: "bold" }}
                    >
                        Reset your password
                    </Typography>

                    {/* <Typography variant="body2" color="text.secondary">
                            Please enter your new password.
                        </Typography>*/}
                </GridItem>
                <GridItem>
                    <Box component="form" onSubmit={handleSubmit}>
                        <DTextField
                            fullWidth
                            type={showPassword ? "text" : "password"}
                            label="New Password"
                            value={password}
                            onChange={(e) => setPassword(e.target.value)}
                            error={!!errors.password}
                            helperText={errors.password}
                            autoComplete="new-password"
                            slotProps={{
                                input: {
                                    endAdornment: (
                                        <InputAdornment position="end">
                                            <IconButton
                                                aria-label="toggle password visibility"
                                                onClick={() =>
                                                    setShowPassword(
                                                        (prev) => !prev
                                                    )
                                                }
                                                edge="end"
                                            >
                                                {showPassword ? (
                                                    <VisibilityOff />
                                                ) : (
                                                    <Visibility />
                                                )}
                                            </IconButton>
                                        </InputAdornment>
                                    )
                                }
                            }}
                        />

                        <Box
                            sx={{
                                p: 1.5,
                                mt: 1,
                                borderRadius: 1,
                                bgcolor: "action.hover",
                                border: "1px solid",
                                borderColor: "divider"
                            }}
                        >
                            <Typography
                                variant="caption"
                                color="text.secondary"
                                sx={{
                                    fontWeight: 600,
                                    display: "block",
                                    mb: 1
                                }}
                            >
                                Password Requirements:
                            </Typography>

                            <Stack spacing={0.5}>
                                {passwordRules.map((rule) => {
                                    const iconColor = !isPasswordTouched
                                        ? "text.disabled"
                                        : rule.met
                                          ? "success.main"
                                          : "error.main";

                                    return (
                                        <Stack
                                            key={rule.label}
                                            direction="row"
                                            alignItems="center"
                                            spacing={1}
                                        >
                                            {rule.met ? (
                                                <CheckCircleOutlineOutlined
                                                    sx={{
                                                        fontSize: 16,
                                                        color: iconColor
                                                    }}
                                                />
                                            ) : (
                                                <CancelOutlined
                                                    sx={{
                                                        fontSize: 16,
                                                        color: iconColor
                                                    }}
                                                />
                                            )}

                                            <Typography
                                                variant="caption"
                                                sx={{
                                                    color: rule.met
                                                        ? "text.primary"
                                                        : "text.secondary",
                                                    fontWeight: rule.met
                                                        ? 500
                                                        : 400
                                                }}
                                            >
                                                {rule.label}
                                            </Typography>
                                        </Stack>
                                    );
                                })}
                            </Stack>
                        </Box>

                        <DTextField
                            fullWidth
                            type={showConfirmPassword ? "text" : "password"}
                            label="Confirm New Password"
                            value={confirmPassword}
                            onChange={(e) => setConfirmPassword(e.target.value)}
                            error={!!errors.confirmPassword}
                            helperText={errors.confirmPassword}
                            autoComplete="new-password"
                            slotProps={{
                                input: {
                                    endAdornment: (
                                        <InputAdornment position="end">
                                            <IconButton
                                                aria-label="toggle confirm password visibility"
                                                onClick={() =>
                                                    setShowConfirmPassword(
                                                        (prev) => !prev
                                                    )
                                                }
                                                edge="end"
                                            >
                                                {showConfirmPassword ? (
                                                    <VisibilityOff />
                                                ) : (
                                                    <Visibility />
                                                )}
                                            </IconButton>
                                        </InputAdornment>
                                    )
                                }
                            }}
                        />

                        {errors.submit && (
                            <Alert severity="error" sx={{ mt: 2 }}>
                                {errors.submit}
                            </Alert>
                        )}

                        <Box sx={{ mt: 3 }}>
                            <PrimaryButton type="submit" loading={loading}>
                                Reset Password
                            </PrimaryButton>
                        </Box>
                    </Box>
                </GridItem>
            </GridContainer>
        </GridItem>
    );

    if (isSubmitted) {
        return (
            <Box px={2} className={classes.root}>
                <LoginCard>
                    <GridContainer spacing={1} style={{ flexWrap: "nowrap" }}>
                        {!isMobile && (
                            <GridItem>
                                <SideCard />
                            </GridItem>
                        )}

                        <GridItem sx={{ flex: 1 }}>
                            <LoginHeader />

                            <Typography
                                variant="body2"
                                color="text.secondary"
                                mb={2}
                                sx={{ fontWeight: "bold" }}
                            >
                                Password Reset Successful
                            </Typography>

                            <Typography variant="body2" color="text.secondary">
                                Your password has been reset successfully. You
                                can now sign in using your new password.
                            </Typography>

                            <Box sx={{ mt: 3 }}>
                                <PrimaryButton
                                    onClick={() => navigate(ROUTES.LOGIN)}
                                >
                                    Back to Login
                                </PrimaryButton>
                            </Box>
                        </GridItem>
                    </GridContainer>
                </LoginCard>
            </Box>
        );
    }

    return (
        <Box px={2} className={classes.root}>
            <LoginCard>
                <GridContainer spacing={1} style={{ flexWrap: "nowrap" }}>
                    {!isMobile && (
                        <GridItem>
                            <SideCard />
                        </GridItem>
                    )}
                    {isValidToken === null ? (
                        renderLoading
                    ) : (
                        <>
                            {renderValid}
                            {renderInvalid}
                        </>
                    )}
                </GridContainer>
            </LoginCard>
        </Box>
    );
}
